import { filter } from '@atlaskit/adf-utils/filter';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { FieldId, FormFields } from '../../types/form/form-fields';
import type { FieldValue, FieldValues } from '../../types/form/form-fields/field-values';
import type { FormSection } from '../../types/form/form-section';
import {
	type FormSectionCondition,
	type FormSectionConditionCheck,
	type FormSectionConditionCheckGroup,
	FormSectionConditionOperator,
	Visibility,
} from '../../types/form/form-section';
import type {
	ConstraintFunction,
	IsConditionSatisfiedOption,
	SectionVisibleStore,
} from '../../types/form/form-section/config';
import { fieldTypeToCheckConfigs } from './fieldTypeToCheckConfigs';

export const filterVisibleSections = (
	formSections: FormSection[],
	formFields: FormFields,
	currentValues: FieldValues,
): FormSection[] => {
	const sectionVisibleStore = createSectionVisibleStore(formSections, formFields, currentValues);

	return formSections.filter((_, index) => isSectionVisible(index, sectionVisibleStore));
};

export const getSectionVisibilityMap = (
	formSections: FormSection[],
	formFields: FormFields,
	currentValues: FieldValues,
): Map<number, boolean> => {
	const sectionVisibleStore = createSectionVisibleStore(formSections, formFields, currentValues);

	return new Map(
		formSections.map((_, index) => [index, isSectionVisible(index, sectionVisibleStore)]),
	);
};

const createSectionVisibleStore = (
	formSections: FormSection[],
	formFields: FormFields,
	currentValues: FieldValues,
): SectionVisibleStore => ({
	// this does not need to be created every render, if doing performance improvements, then pass question to section index map in
	questionToSectionIndexMap: createQuestionToSectionIndexMap(formSections),
	sectionVisibleCache: new Map(),
	sections: formSections,
	formFields,
	currentValues,
});

export const isSectionVisible = (
	sectionIndex: number,
	sectionVisibleStore: SectionVisibleStore,
): boolean => {
	const cached = sectionVisibleStore.sectionVisibleCache.get(sectionIndex);
	if (cached) {
		return cached;
	}

	const section = sectionVisibleStore.sections[sectionIndex];

	const value =
		!section.condition ||
		isVisible(section.condition, isConditionSatisfied(section.condition, sectionVisibleStore));

	sectionVisibleStore.sectionVisibleCache.set(sectionIndex, value);
	return value;
};

const isVisible = (condition: FormSectionCondition, data: IsConditionSatisfiedOption) => {
	if (data.hasError) {
		// a condition either shows or hides a section. By default the condition is essentially 'off'
		// When the condition is set to 'hide' then the 'off' setting is to show the section.
		return condition.visibility === Visibility.HIDE;
	}

	return (
		(condition.visibility === Visibility.SHOW && data.value) ||
		(condition.visibility === Visibility.HIDE && !data.value)
	);
};

// New implementation of advanced conditional logic
// Evaluates a condition defined as groups of checks with logical operators between
// checks within a group and between groups themselves.
export const isConditionSatisfied = (
	condition: FormSectionCondition,
	sectionVisibleStore: SectionVisibleStore,
): IsConditionSatisfiedOption => {
	let accumulatedResult: IsConditionSatisfiedOption = {
		value: getInitialValue(condition.operator),
		hasError: false,
	};

	for (let i = 0; i < condition.checkGroups.length; i += 1) {
		const checkGroup = condition.checkGroups[i];
		const groupResult = evaluateCheckGroup(checkGroup, sectionVisibleStore);
		// Propagate errors immediately
		if (groupResult.hasError) {
			return groupResult;
		}

		// short circuit for OR operator
		if (condition.operator === FormSectionConditionOperator.OR && groupResult.value === true) {
			return groupResult;
		}

		accumulatedResult = {
			value: applyLogicalOperator(accumulatedResult.value, groupResult.value, condition.operator),
			hasError: false,
		};
	}

	return accumulatedResult;
};

const evaluateCheckGroup = (
	checkGroup: FormSectionConditionCheckGroup,
	sectionVisibleStore: SectionVisibleStore,
): IsConditionSatisfiedOption => {
	let result: IsConditionSatisfiedOption = {
		value: getInitialValue(checkGroup.operator),
		hasError: false,
	};

	for (let j = 0; j < checkGroup.checks.length; j += 1) {
		const check = checkGroup.checks[j];

		const checkResult = calculateCheck(check, sectionVisibleStore);

		// Short-circuit on error to bubble up
		if (checkResult.hasError) {
			return checkResult;
		}

		result = {
			value: applyLogicalOperator(result.value, checkResult.value, checkGroup.operator),
			hasError: false,
		};
	}

	return result;
};

// Identity elements for logical operators so we can start with a value that won't influence the result
const getInitialValue = (operator: FormSectionConditionOperator): boolean => {
	switch (operator) {
		case FormSectionConditionOperator.AND:
			return true; // true AND condition = condition
		case FormSectionConditionOperator.OR:
			return false; // false OR condition = condition
		default:
			return true;
	}
};

const applyLogicalOperator = (
	previousValue: boolean,
	currentValue: boolean,
	operator: FormSectionConditionOperator,
) => {
	switch (operator) {
		case FormSectionConditionOperator.AND:
			return previousValue && currentValue;
		case FormSectionConditionOperator.OR:
			return previousValue || currentValue;
		default:
			return currentValue;
	}
};

const calculateCheck = (
	conditionCheck: FormSectionConditionCheck,
	sectionVisibleStore: SectionVisibleStore,
): IsConditionSatisfiedOption => {
	const formField = sectionVisibleStore.formFields[conditionCheck.fieldId];
	if (formField === undefined) {
		return { value: false, hasError: true };
	}

	const fieldType = formField.type;
	const fieldValue = sectionVisibleStore.currentValues[conditionCheck.fieldId];

	const config = fieldTypeToCheckConfigs[fieldType] ?? undefined;

	if (config === undefined) {
		return { value: false, hasError: true };
	}

	const constraintCheck = config.constraintChecks?.[conditionCheck.type] ?? undefined;
	if (constraintCheck === undefined) {
		return { value: false, hasError: true };
	}

	const { constraint } = conditionCheck;
	if (fieldValue !== undefined && !config.typeGuard(fieldValue)) {
		return { value: false, hasError: true };
	}

	const parentSectionIndex = sectionVisibleStore.questionToSectionIndexMap.get(
		conditionCheck.fieldId,
	);

	let value = (constraintCheck as ConstraintFunction<FieldValue>)(fieldValue, constraint);

	if (value && parentSectionIndex !== undefined) {
		const isParentVisible = isSectionVisible(parentSectionIndex, sectionVisibleStore);
		value = isParentVisible;
	}

	return { value, hasError: false };
};

const createQuestionToSectionIndexMap = (formSections: FormSection[]): Map<FieldId, number> => {
	const questionToSectionIndexMap = new Map();

	formSections.forEach((section, index) =>
		section.layout.content.forEach((contentNode) =>
			// this handles recursive sections
			filter(contentNode, (node: ADFEntity) => {
				if (!node.attrs || !node.attrs.parameters || !node.attrs.parameters.id) {
					return false;
				}
				return node.attrs.extensionKey === 'question';
			}).forEach((node: ADFEntity) => {
				const id = node.attrs?.parameters?.id;
				if (id) {
					questionToSectionIndexMap.set(id.toString(), index);
				}
			}),
		),
	);

	return questionToSectionIndexMap;
};
